import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReminderTriggerFormItem.module.scss';
import { Col, Row } from 'react-flexbox-grid';
import { ReactComponent as TrashIcon } from 'assets/svg/trash_no_color.svg';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import InputError from 'common/components/inputError/InputError';
import { Controller, UseFormMethods } from 'react-hook-form';
import Input from 'common/components/input/Input';
import Select from 'common/components/select/Select';
import CreateSelect from 'common/components/select/CreateSelect';
import InputGroupController from 'common/components/inputGroup/InputGroupController';
import InputInfoWarning from 'common/components/inputInfoWarning/InputInfoWarning';
import DateTimePickerControllerNew from 'common/components/dateTimePicker/DateTimePickerControllerNew';
import ReactTooltip from 'react-tooltip';
import { FaCalendarAlt, FaCheckCircle, FaChevronDown, FaUserFriends } from 'react-icons/fa';
import { removeAccents, DATE_FORMAT_DEFAUT } from 'Config';
import { ReminderContactViewModel } from 'api/reminders/models/ReminderViewModel';
import DateWithHourController from 'common/components/dateWithHour/DateWithHourController';
import InputGroup from 'common/components/inputGroup/InputGroup';

type Props = {
    form: UseFormMethods<any>;
    isDetails?: boolean
    onRemoveClick: () => void;
    index: number;
    displayIndex: number;
    triggersCounter: number,
    criteriaOptions: SelectValueLabel[];
    measureOptions: SelectValueLabel[];
    responsablesOptions: SelectValueLabel[];
    onCreateResponsableOption: (value: any) => void;
    collapseAllCounter?: number;
    isLastOfControl?: boolean;
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const formatKm = (value: number) => {
    return value.toLocaleString('pt-PT');
};

const formatDateTime = (date: Date): string => {
    const d = new Date(date);
    return `${d.toLocaleDateString()} | ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const ReminderTriggerFormItem: React.FC<Props> = ({
    form,
    isDetails,
    onRemoveClick,
    index,
    displayIndex,
    triggersCounter: _triggersCounter,
    criteriaOptions,
    measureOptions,
    responsablesOptions,
    onCreateResponsableOption,
    collapseAllCounter,
    isLastOfControl,
    ..._props
}: Props) => {
    const { t } = useTranslation();
    const errors = (form.errors?.triggers || [])[index] || null;
    const [criteria, setCriteria] = useState<string | undefined>();
    const isNewlyAdded = (collapseAllCounter ?? 0) > 0 && isLastOfControl;
    const [collapsed, setCollapsed] = useState<boolean>(!isNewlyAdded);
    const prevCollapseCounter = useRef(collapseAllCounter);

    useEffect(() => {
        if (collapseAllCounter !== prevCollapseCounter.current) {
            prevCollapseCounter.current = collapseAllCounter;
            setCollapsed(!isLastOfControl);
        }
    }, [collapseAllCounter, isLastOfControl]);

    useEffect(() => {
        form.setValue(`triggers[${index}].index`, index);
    }, [index]);

    const watchFirstKm = form.watch(`triggers[${index}].firstKm`);
    const watchBeforeKm = form.watch(`triggers[${index}].beforeKm`);
    const watchRepeatKm = form.watch(`triggers[${index}].repeatKm`);
    const watchStopKm = form.watch(`triggers[${index}].stopKm`);
    const watchFirstHour = form.watch(`triggers[${index}].firstHour`);
    const watchBeforeHour = form.watch(`triggers[${index}].beforeHour`);
    const watchRepeatHour = form.watch(`triggers[${index}].repeatHour`);
    const watchStopHour = form.watch(`triggers[${index}].stopHour`);
    const watchFirstDate = form.watch(`triggers[${index}].firstDate`);
    const watchBeforeDateValue = form.watch(`triggers[${index}].beforeDateValue`);
    const watchBeforeDateMeasure = form.watch(`triggers[${index}].beforeDateMeasure`);
    const watchRepeatDateValue = form.watch(`triggers[${index}].repeatDateValue`);
    const watchRepeatDateMeasure = form.watch(`triggers[${index}].repeatDateMeasure`);
    const watchStopDate = form.watch(`triggers[${index}].stopDate`);
    const watchContacts: ReminderContactViewModel[] = form.watch(`triggers[${index}].contacts`) ?? [];

    useEffect(() => {
        const _criteria = form.getValues(`triggers[${index}].criteria`);
        setCriteria(!(_criteria === 'DATE' || _criteria === 'KMS' || _criteria === 'HOURS') ? 'DATE' : _criteria)
    }, []);

    useEffect(() => {
        if (criteria == 'KMS' && !criteriaOptions.find(x => x.value == 'KMS')) {
            form.setValue(`triggers[${index}].criteria`, 'DATE');
            setCriteria('DATE')
        }
        if (criteria == 'HOURS' && !criteriaOptions.find(x => x.value == 'HOURS')) {
            form.setValue(`triggers[${index}].criteria`, 'DATE');
            setCriteria('DATE')
        }
    }, [criteriaOptions]);

    useEffect(() => {
        const _criteria = form.getValues(`triggers[${index}].criteria`);
        if (_criteria === 'DATE') {
            const beforeDateMeasure = form.getValues(`triggers[${index}].beforeDateMeasure`);
            const repeatDateMeasure = form.getValues(`triggers[${index}].repeatDateMeasure`);
            if (!beforeDateMeasure) {
                form.setValue(`triggers[${index}].beforeDateMeasure`, 'DAY')
            }
            if (!repeatDateMeasure) {
                form.setValue(`triggers[${index}].repeatDateMeasure`, 'DAY')
            }
        }
    }, [criteria]);

    useEffect(() => {
        if (watchStopKm && watchRepeatKm) {
            form.trigger(`triggers[${index}].stopKm`);
        }
    }, [watchFirstKm, watchBeforeKm]);

    useEffect(() => {
        if (watchStopHour && watchRepeatHour) {
            form.trigger(`triggers[${index}].stopHour`);
        }
    }, [watchFirstHour, watchBeforeHour]);

    const getCriteriaLabel = () => {
        const opt = criteriaOptions.find(x => x.value === criteria);
        return opt?.label ?? '';
    };

    const getFirstAlertSummary = (): string => {
        if (criteria === 'KMS') {
            const km = Number(watchFirstKm);
            return km ? `${formatKm(km)} km` : '';
        }
        if (criteria === 'HOURS') {
            const hour = Number(watchFirstHour);
            return hour ? `${formatKm(hour)} h` : '';
        }
        if (criteria === 'DATE' && watchFirstDate) {
            return formatDateTime(new Date(watchFirstDate));
        }
        return '';
    };

    const emailCount = watchContacts?.length ?? 0;

    const renderKmsPreview = () => {
        const firstKm = Number(watchFirstKm);
        if (!firstKm) return null;

        const beforeKm = Number(watchBeforeKm);
        const repeatKm = Number(watchRepeatKm);
        const stopKm = Number(watchStopKm);
        const parts: string[] = [];

        if (beforeKm && beforeKm < firstKm) {
            const alertKm = firstKm - beforeKm;
            parts.push(t('reminders.notifications.preview.alert_at_km_before', {
                alertKm: formatKm(alertKm),
                beforeKm: formatKm(beforeKm),
                firstKm: formatKm(firstKm)
            }) as string);
        } else {
            parts.push(t('reminders.notifications.preview.alert_at_km', { km: formatKm(firstKm) }) as string);
        }

        if (repeatKm && repeatKm > 0) {
            parts.push(t('reminders.notifications.preview.repeat_every_km', { km: formatKm(repeatKm) }) as string);
        }

        if (stopKm && stopKm >= firstKm) {
            parts.push(t('reminders.notifications.preview.stops_at_km', { km: formatKm(stopKm) }) as string);
        }

        return (
            <div className={styles.previewBox}>
                <FaCheckCircle className={styles.previewIcon} />
                <span dangerouslySetInnerHTML={{ __html: parts.join(' ') }} />
            </div>
        );
    };

    const renderHoursPreview = () => {
        const firstHour = Number(watchFirstHour);
        if (!firstHour) return null;

        const beforeHour = Number(watchBeforeHour);
        const repeatHour = Number(watchRepeatHour);
        const stopHour = Number(watchStopHour);
        const parts: string[] = [];

        if (beforeHour && beforeHour < firstHour) {
            const alertHour = firstHour - beforeHour;
            parts.push(t('reminders.notifications.preview.alert_at_hour_before', {
                alertHour: formatKm(alertHour),
                beforeHour: formatKm(beforeHour),
                firstHour: formatKm(firstHour)
            }) as string);
        } else {
            parts.push(t('reminders.notifications.preview.alert_at_hour', { hour: formatKm(firstHour) }) as string);
        }

        if (repeatHour && repeatHour > 0) {
            parts.push(t('reminders.notifications.preview.repeat_every_hour', { hour: formatKm(repeatHour) }) as string);
        }

        if (stopHour && stopHour >= firstHour) {
            parts.push(t('reminders.notifications.preview.stops_at_hour', { hour: formatKm(stopHour) }) as string);
        }

        return (
            <div className={styles.previewBox}>
                <FaCheckCircle className={styles.previewIcon} />
                <span dangerouslySetInnerHTML={{ __html: parts.join(' ') }} />
            </div>
        );
    };

    const subtractFromDate = (date: Date, value: number, measure: string): Date => {
        const d = new Date(date);
        if (measure === 'DAY') d.setDate(d.getDate() - value);
        else if (measure === 'WEEK') d.setDate(d.getDate() - value * 7);
        else if (measure === 'MONTH') d.setMonth(d.getMonth() - value);
        else if (measure === 'YEAR') d.setFullYear(d.getFullYear() - value);
        return d;
    };

    const renderDatePreview = () => {
        if (!watchFirstDate) return null;

        const firstDate = new Date(watchFirstDate);
        const formattedDate = formatDateTime(firstDate);
        const parts: string[] = [];

        const beforeVal = Number(watchBeforeDateValue);
        if (beforeVal && beforeVal > 0 && watchBeforeDateMeasure) {
            const alertDate = subtractFromDate(firstDate, beforeVal, watchBeforeDateMeasure);
            if (alertDate < firstDate) {
                parts.push(t('reminders.notifications.preview.alert_at_date_before', {
                    alertDate: formatDateTime(alertDate),
                    date: formattedDate
                }) as string);
            } else {
                parts.push(t('reminders.notifications.preview.alert_at_date', { date: formattedDate }) as string);
            }
        } else {
            parts.push(t('reminders.notifications.preview.alert_at_date', { date: formattedDate }) as string);
        }

        const repeatVal = Number(watchRepeatDateValue);
        if (repeatVal && repeatVal > 0 && watchRepeatDateMeasure) {
            const measureLabel = measureOptions.find(x => x.value === watchRepeatDateMeasure)?.label ?? watchRepeatDateMeasure;
            parts.push(t('reminders.notifications.preview.repeat_every_date', {
                value: repeatVal,
                unit: measureLabel?.toLowerCase()
            }) as string);
        }

        if (watchStopDate) {
            const stopDate = new Date(watchStopDate);
            parts.push(t('reminders.notifications.preview.stops_at_date', { date: stopDate.toLocaleDateString() }) as string);
        }

        return (
            <div className={styles.previewBox}>
                <FaCheckCircle className={styles.previewIcon} />
                <span dangerouslySetInnerHTML={{ __html: parts.join(' ') }} />
            </div>
        );
    };

    return (
        <div className={`${styles.triggerCard} ${(errors && styles.triggerCardError) || ''}`}>

            {/* Header: chevron + number + criteria label + delete */}
            <div className={styles.triggerHeader} onClick={() => setCollapsed(!collapsed)} style={{ cursor: 'pointer' }}>
                <div className={styles.triggerHeaderLeft}>
                    <FaChevronDown className={`${styles.headerChevron} ${!collapsed ? styles.headerChevronOpen : ''}`} />
                    <span className={styles.triggerNumber}>{displayIndex}</span>
                    <span className={styles.triggerCriteriaLabel}>
                        {getCriteriaLabel()}
                    </span>
                    {getFirstAlertSummary() && (
                        <div className={styles.headerDateWrap}>
                            <span className={styles.headerMeta}>{getFirstAlertSummary()}</span>
                            <div className={styles.headerDateTooltip}>
                                {t('reminders.notifications.first_date')}:&nbsp;<strong>{criteria === 'DATE' && watchFirstDate ? formatDateTime(new Date(watchFirstDate)) : getFirstAlertSummary()}</strong>
                                {criteria === 'DATE' && watchFirstDate && Number(watchBeforeDateValue) > 0 && watchBeforeDateMeasure && (() => {
                                    const alertDate = subtractFromDate(new Date(watchFirstDate), Number(watchBeforeDateValue), watchBeforeDateMeasure);
                                    return alertDate < new Date(watchFirstDate)
                                        ? <><br />{t('reminders.notifications.alert_date_label' as any)}:&nbsp;<strong>{formatDateTime(alertDate)}</strong></>
                                        : null;
                                })()}
                                {criteria === 'KMS' && Number(watchBeforeKm) > 0 && Number(watchFirstKm) > 0 && (() => {
                                    const alertKm = Number(watchFirstKm) - Number(watchBeforeKm);
                                    return alertKm > 0
                                        ? <><br />{t('reminders.notifications.alert_date_label' as any)}:&nbsp;<strong>{formatKm(alertKm)} km</strong></>
                                        : null;
                                })()}
                                {criteria === 'HOURS' && Number(watchBeforeHour) > 0 && Number(watchFirstHour) > 0 && (() => {
                                    const alertHour = Number(watchFirstHour) - Number(watchBeforeHour);
                                    return alertHour > 0
                                        ? <><br />{t('reminders.notifications.alert_date_label' as any)}:&nbsp;<strong>{formatKm(alertHour)} h</strong></>
                                        : null;
                                })()}
                            </div>
                        </div>
                    )}
                    {emailCount > 0 && (
                        <div className={styles.headerEmailsWrap}>
                            <FaUserFriends className={styles.headerEmailsIcon} />
                            <span className={styles.headerMeta}>{emailCount}</span>
                            <div className={styles.headerEmailsTooltip}>
                                {watchContacts.map(c => {
                                    const opt = responsablesOptions.find(o => o.value === c.email);
                                    const hasName = opt && opt.label !== opt.value;
                                    return <div key={c.email}>
                                        {hasName
                                            ? <><strong>{opt!.label.substring(0, opt!.label.lastIndexOf('(')).trim()}</strong> <span style={{ color: '#999' }}>({c.email})</span></>
                                            : c.email}
                                    </div>;
                                })}
                            </div>
                        </div>
                    )}
                </div>
                {!isDetails && (
                    <>
                        <button type="button" className={styles.deleteButton} onClick={e => { e.stopPropagation(); onRemoveClick(); }}>
                            <TrashIcon className={styles.deleteIcon} />
                            <span>{t('reminders.notifications.delete_one')}</span>
                        </button>
                        <ReactTooltip place="top" type="dark" effect="solid" />
                    </>
                )}
            </div>

            <div style={{ display: collapsed ? 'none' : undefined }}>
            <hr className={styles.triggerDivider} />

            {/* Criteria + Email on same row */}
            <Row className={styles.rowSpace}>
                <Col xs={12} lg={2}>
                    <FormItem>
                        <Label className={styles.label}>{t('reminders.notifications.criteria.title')} {!isDetails ? '*' : ''}</Label>
                        <Controller
                            render={({ onChange: _onChange, value }) => {
                                if (criteriaOptions.length >= 3) {
                                    return (
                                        <Select
                                            options={criteriaOptions}
                                            placeholder={t('reminders.notifications.criteria.title')}
                                            isDisabled={isDetails}
                                            isClearable={false}
                                            onChange={(data: SelectValueLabel) => {
                                                if (!data) return;
                                                form.setValue(`triggers[${index}].criteria`, data.value);
                                                setCriteria(data.value);
                                            }}
                                            value={criteriaOptions.find(x => x.value === value) ?? null}
                                        />
                                    );
                                }
                                return (
                                    <div className={styles.criteriaPills}>
                                        {criteriaOptions.map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`${styles.criteriaPill} ${value === opt.value ? styles.criteriaPillActive : ''}`}
                                                disabled={isDetails}
                                                onClick={() => {
                                                    form.setValue(`triggers[${index}].criteria`, opt.value);
                                                    setCriteria(opt.value);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                );
                            }}
                            control={form.control}
                            name={`triggers[${index}].criteria`}
                            defaultValue={form.getValues(`triggers[${index}].criteria`)} />
                        <InputError error={errors?.criteria} />
                    </FormItem>
                </Col>
                <Col xs={12} lg={10}>
                    <FormItem>
                        <Label className={styles.label}>{t('reminders.notifications.email_label')} {!isDetails ? '*' : ''}</Label>
                        <Controller
                            render={({ onChange, value, ref }) => {
                                return (
                                    <CreateSelect
                                        options={responsablesOptions}
                                        innerRef={ref}
                                        isClearable
                                        isMulti
                                        isDisabled={isDetails}
                                        placeholder={t('reminders.contacts.email')}
                                        styles={errors?.contacts ? { control: (base: any) => ({ ...base, borderColor: '#e8a824 !important' }) } : undefined}
                                        onChange={(data: SelectValueLabel[]) => {
                                            onChange(data?.map(x => ({ email: x.value, phone: '' })));
                                        }}
                                        value={value ? responsablesOptions.filter(x => value.find((y: ReminderContactViewModel) => { return y.email === x.value })) : null}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onCreateOption={(newValue: any) => {
                                            onCreateResponsableOption(newValue);
                                            onChange([...watchContacts, { email: newValue, phone: '' }])
                                        }}
                                        formatOptionLabel={(option: any, { context }: any) => {
                                            if (context === 'value') {
                                                const hasName = option.label !== option.value;
                                                return <span>{hasName ? option.label.substring(0, option.label.lastIndexOf('(')).trim() : option.value}</span>;
                                            }
                                            const hasName = option.label !== option.value;
                                            if (hasName) {
                                                const name = option.label.substring(0, option.label.lastIndexOf('('));
                                                return <span><strong>{name.trim()}</strong> <span style={{ color: '#666' }}>({option.value})</span></span>;
                                            }
                                            return <span>{option.value}</span>;
                                        }}
                                    />
                                );
                            }}
                            control={form.control}
                            name={`triggers[${index}].contacts`}
                            rules={{ validate: { required: (value: any) => { return value ? (value && value.length > 0) : false; } } }}
                        />
                        <InputError error={errors?.contacts} />
                    </FormItem>
                </Col>
            </Row>

            {/* Designation */}
            <Row className={styles.rowSpace}>
                <Col xs={12}>
                    <FormItem>
                        <Label className={styles.label}>{t('reminders.notifications.designation')} {!isDetails ? '*' : ''}</Label>
                        <Controller
                            render={({ ref, onChange: _onChange, value }) => {
                                return (
                                    <Input
                                        ref={ref}
                                        disabled={isDetails}
                                        form={undefined}
                                        onChange={(e: any) => {
                                            form.setValue(`triggers[${index}].designation`, e.target.value);
                                        }}
                                        value={value}
                                        className={errors?.designation ? styles.inputWarning : undefined}
                                    />
                                );
                            }}
                            control={form.control}
                            name={`triggers[${index}].designation`}
                            defaultValue={form.getValues(`triggers[${index}].designation`)}
                            rules={{ maxLength: 200, required: true }}
                            placeholder={criteria === 'KMS'
                                ? t('reminders.notifications.placeholders.designation_kms') as string
                                : criteria === 'HOURS'
                                    ? t('reminders.notifications.placeholders.designation_hours') as string
                                    : t('reminders.notifications.placeholders.designation_date') as string
                            } />
                        <InputError error={errors?.designation} />
                    </FormItem>
                </Col>
            </Row>

            {/* When to alert | Advance notice & repetition — side by side, 4 columns */}
            <Row className={criteria === 'DATE' ? styles.dateAlertRow : undefined}>
                {criteria === 'HOURS' && (
                    <>
                        <Col xs={12} lg={6}>
                            <div className={styles.groupToggle}>
                                <span className={styles.groupLabel}>{t('reminders.notifications.section_when')}</span>
                            </div>
                            <Row>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.firstHour')} {!isDetails ? '*' : ''}</Label>
                                        <InputGroupController
                                            text={t('common.hours_symbol') as string}
                                            placeholder={t('reminders.notifications.placeholders.firstHour') as string}
                                            name={`triggers[${index}].firstHour`}
                                            form={form}
                                            disabled={isDetails}
                                            format="integer"
                                            warningBorder={!!errors?.firstHour}
                                            rules={{ required: true }}
                                        />
                                        <InputError error={errors?.firstHour} />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.firstHour') as string} />}
                                    </FormItem>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.beforeHour')}</Label>
                                        <InputGroupController
                                            text={t('common.hours_symbol') as string}
                                            placeholder={t('reminders.notifications.placeholders.beforeHour') as string}
                                            name={`triggers[${index}].beforeHour`}
                                            form={form}
                                            disabled={isDetails}
                                            format="integer"
                                        />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.beforeHour') as string} />}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={12} lg={6}>
                            <div className={styles.groupToggle}>
                                <span className={styles.groupLabel}>{t('reminders.notifications.section_schedule')}</span>
                            </div>
                            <Row>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.repeatHour')}</Label>
                                        <InputGroupController
                                            text={t('common.hours_symbol') as string}
                                            placeholder={t('reminders.notifications.placeholders.repeatHour') as string}
                                            name={`triggers[${index}].repeatHour`}
                                            form={form}
                                            disabled={isDetails}
                                            format="integer"
                                        />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.repeatHour') as string} />}
                                    </FormItem>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.stopHour')} {!isDetails && !!watchRepeatHour ? '*' : ''}</Label>
                                        <div className={styles.stopKmWrap}>
                                            <InputGroupController
                                                text={t('common.hours_symbol') as string}
                                                placeholder={t('reminders.notifications.placeholders.stopHour') as string}
                                                name={`triggers[${index}].stopHour`}
                                                form={form}
                                                disabled={isDetails || !watchRepeatHour}
                                                format="integer"
                                                warningBorder={(() => {
                                                    if (!watchStopHour || !watchRepeatHour) return false;
                                                    const stopVal = Number(watchStopHour);
                                                    const firstVal = Number(watchFirstHour) || 0;
                                                    const beforeVal = Number(watchBeforeHour) || 0;
                                                    return stopVal < (firstVal - beforeVal);
                                                })()}
                                                rules={{
                                                    required: !!watchRepeatHour,
                                                    validate: {
                                                        minValue: (value: any) => {
                                                            if (!value || !watchRepeatHour) return true;
                                                            const stopVal = Number(value);
                                                            const firstVal = Number(watchFirstHour) || 0;
                                                            const beforeVal = Number(watchBeforeHour) || 0;
                                                            const minStop = firstVal - beforeVal;
                                                            if (stopVal < minStop) {
                                                                return `${t('reminders.notifications.stop_hour_min_error' as any, { value: formatKm(minStop) })}`;
                                                            }
                                                            return true;
                                                        }
                                                    }
                                                }}
                                            />
                                            {(() => {
                                                const firstVal = Number(watchFirstHour) || 0;
                                                const beforeVal = Number(watchBeforeHour) || 0;
                                                const minStop = firstVal - beforeVal;
                                                return minStop > 0
                                                    ? <div className={styles.stopKmTooltip}>
                                                        {t('reminders.notifications.stop_hour_min_error' as any, { value: formatKm(minStop) })}
                                                    </div>
                                                    : null;
                                            })()}
                                        </div>
                                        <InputError error={errors?.stopHour} />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.stopHour') as string} />}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                    </>
                )}
                {criteria === 'KMS' && (
                    <>
                        <Col xs={12} lg={6}>
                            <div className={styles.groupToggle}>
                                <span className={styles.groupLabel}>{t('reminders.notifications.section_when')}</span>
                            </div>
                            <Row>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.firstKm')} {!isDetails ? '*' : ''}</Label>
                                        <InputGroupController
                                            text={t('common.kms') as string}
                                            placeholder={t('reminders.notifications.placeholders.firstKm') as string}
                                            name={`triggers[${index}].firstKm`}
                                            form={form}
                                            disabled={isDetails}
                                            format="integer"
                                            warningBorder={!!errors?.firstKm}
                                            rules={{ required: true }}
                                        />
                                        <InputError error={errors?.firstKm} />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.firstKm') as string} />}
                                    </FormItem>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.beforeKm')}</Label>
                                        <InputGroupController
                                            text={t('common.kms') as string}
                                            placeholder={t('reminders.notifications.placeholders.beforeKm') as string}
                                            name={`triggers[${index}].beforeKm`}
                                            form={form}
                                            disabled={isDetails}
                                            format="integer"
                                        />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.beforeKm') as string} />}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={12} lg={6}>
                            <div className={styles.groupToggle}>
                                <span className={styles.groupLabel}>{t('reminders.notifications.section_schedule')}</span>
                            </div>
                            <Row>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.repeatKm')}</Label>
                                        <InputGroupController
                                            text={t('common.kms') as string}
                                            placeholder={t('reminders.notifications.placeholders.repeatKm') as string}
                                            name={`triggers[${index}].repeatKm`}
                                            form={form}
                                            disabled={isDetails}
                                            format="integer"
                                        />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.repeatKm') as string} />}
                                    </FormItem>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.stopKm')} {!isDetails && !!watchRepeatKm ? '*' : ''}</Label>
                                        <div className={styles.stopKmWrap}>
                                            <InputGroupController
                                                text={t('common.kms') as string}
                                                placeholder={t('reminders.notifications.placeholders.stopKm') as string}
                                                name={`triggers[${index}].stopKm`}
                                                form={form}
                                                disabled={isDetails || !watchRepeatKm}
                                                format="integer"
                                                warningBorder={(() => {
                                                    if (!watchStopKm || !watchRepeatKm) return false;
                                                    const stopVal = Number(watchStopKm);
                                                    const firstVal = Number(watchFirstKm) || 0;
                                                    const beforeVal = Number(watchBeforeKm) || 0;
                                                    return stopVal < (firstVal - beforeVal);
                                                })()}
                                                rules={{
                                                    required: !!watchRepeatKm,
                                                    validate: {
                                                        minValue: (value: any) => {
                                                            if (!value || !watchRepeatKm) return true;
                                                            const stopVal = Number(value);
                                                            const firstVal = Number(watchFirstKm) || 0;
                                                            const beforeVal = Number(watchBeforeKm) || 0;
                                                            const minStop = firstVal - beforeVal;
                                                            if (stopVal < minStop) {
                                                                return `${t('reminders.notifications.stop_km_min_error' as any, { value: formatKm(minStop) })}`;
                                                            }
                                                            return true;
                                                        }
                                                    }
                                                }}
                                            />
                                            {(() => {
                                                const firstVal = Number(watchFirstKm) || 0;
                                                const beforeVal = Number(watchBeforeKm) || 0;
                                                const minStop = firstVal - beforeVal;
                                                return minStop > 0
                                                    ? <div className={styles.stopKmTooltip}>
                                                        {t('reminders.notifications.stop_km_min_error' as any, { value: formatKm(minStop) })}
                                                    </div>
                                                    : null;
                                            })()}
                                        </div>
                                        <InputError error={errors?.stopKm} />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.stopKm') as string} />}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                    </>
                )}
                {criteria === 'DATE' && (
                    <>
                        <Col xs={12} lg={7}>
                            <div className={styles.groupToggle}>
                                <span className={styles.groupLabel}>{t('reminders.notifications.section_when')}</span>
                            </div>
                            <Row>
                                <Col xs={12} lg={7}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.first_date')} {!isDetails ? '*' : ''}</Label>
                                        <DateWithHourController
                                            form={form}
                                            datePlaceholder={t('reminders.notifications.first_date') as string}
                                            dateFormat={DATE_FORMAT_DEFAUT}
                                            dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                                            name={`triggers[${index}].firstDate`}
                                            timePlaceholder={t('common.hour') as string}
                                            isDisabled={isDetails ?? false}
                                            minDate={new Date()}
                                            className={errors?.firstDate ? styles.inputWarning : undefined}
                                            rules={{ required: true }}
                                        />
                                        <InputError error={errors?.firstDate} />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.firstDate') as string} />}
                                    </FormItem>
                                </Col>
                                <Col xs={12} lg={5}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.beforeDateValue')}</Label>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ flex: '0 0 95px' }}>
                                                <Controller
                                                    render={({ ref, onChange, value }) => {
                                                        return (
                                                            <Input
                                                                ref={ref}
                                                                disabled={isDetails}
                                                                form={undefined}
                                                                onChange={(e: any) => {
                                                                    onChange(e.target.value);
                                                                }}
                                                                type="number"
                                                                value={value}
                                                            />
                                                        );
                                                    }}
                                                    control={form.control}
                                                    name={`triggers[${index}].beforeDateValue`}
                                                    defaultValue={form.getValues(`triggers[${index}].beforeDateValue`)}
                                                    rules={{ maxLength: 7 }}
                                                    placeholder={t('reminders.notifications.beforeDateValue')}
                                                />
                                            </div>
                                            <div style={{ flex: '1' }}>
                                                <Controller
                                                    render={({ onChange, value }) => {
                                                        return (
                                                            <Select
                                                                options={measureOptions}
                                                                placeholder={t('reminders.notifications.criteria.title')}
                                                                isDisabled={isDetails}
                                                                onChange={(data: SelectValueLabel) => {
                                                                    onChange(data ? data.value : null);
                                                                }}
                                                                value={measureOptions.find(x => x.value === value) ? { label: measureOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                            />
                                                        );
                                                    }}
                                                    control={form.control}
                                                    rules={{ maxLength: 10, required: true }}
                                                    name={`triggers[${index}].beforeDateMeasure`}
                                                    defaultValue={form.getValues(`triggers[${index}].beforeDateMeasure`)} />
                                            </div>
                                        </div>
                                        {<InputInfoWarning message={t('reminders.notifications.hints.beforeDate') as string} />}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={12} lg={5}>
                            <div className={styles.groupToggle}>
                                <span className={styles.groupLabel}>{t('reminders.notifications.section_schedule')}</span>
                            </div>
                            <Row>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.repeatDateValue')}</Label>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ flex: '0 0 95px' }}>
                                                <Controller
                                                    render={({ ref, onChange, value }) => {
                                                        return (
                                                            <Input
                                                                ref={ref}
                                                                disabled={isDetails}
                                                                form={undefined}
                                                                onChange={(e: any) => {
                                                                    onChange(e.target.value);
                                                                }}
                                                                type="number"
                                                                value={value}
                                                            />
                                                        );
                                                    }}
                                                    control={form.control}
                                                    name={`triggers[${index}].repeatDateValue`}
                                                    defaultValue={form.getValues(`triggers[${index}].repeatDateValue`)}
                                                    rules={{ maxLength: 7 }}
                                                    placeholder={t('reminders.notifications.repeatDateValue')}
                                                />
                                            </div>
                                            <div style={{ flex: '1' }}>
                                                <Controller
                                                    render={({ onChange, value }) => {
                                                        return (
                                                            <Select
                                                                options={measureOptions}
                                                                placeholder={t('reminders.notifications.criteria.title')}
                                                                isDisabled={isDetails}
                                                                onChange={(data: SelectValueLabel) => {
                                                                    onChange(data ? data.value : null);
                                                                }}
                                                                value={measureOptions.find(x => x.value === value) ? { label: measureOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                            />
                                                        );
                                                    }}
                                                    control={form.control}
                                                    rules={{ maxLength: 10, required: true }}
                                                    name={`triggers[${index}].repeatDateMeasure`}
                                                    defaultValue={form.getValues(`triggers[${index}].repeatDateMeasure`)} />
                                            </div>
                                        </div>
                                        {<InputInfoWarning message={t('reminders.notifications.hints.repeatDate') as string} />}
                                    </FormItem>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('reminders.notifications.stopDate')} {!isDetails && !!watchRepeatDateValue ? '*' : ''}</Label>
                                        <div className={errors?.stopDate ? styles.inputWarning : undefined}>
                                            <DateTimePickerControllerNew
                                                form={form}
                                                placeholderText={t('reminders.notifications.stopDate')}
                                                name={`triggers[${index}].stopDate`}
                                                autoComplete='off'
                                                disabled={isDetails || !watchRepeatDateValue}
                                                minDate={(() => {
                                                    const fd = watchFirstDate ? new Date(watchFirstDate) : undefined;
                                                    if (!fd) return undefined;
                                                    const beforeVal = Number(watchBeforeDateValue);
                                                    if (beforeVal > 0 && watchBeforeDateMeasure) {
                                                        return subtractFromDate(fd, beforeVal, watchBeforeDateMeasure);
                                                    }
                                                    return fd;
                                                })()}
                                                rules={{ required: !!watchRepeatDateValue }} />
                                        </div>
                                        <InputError error={errors?.stopDate} />
                                        {<InputInfoWarning message={t('reminders.notifications.hints.stopDate') as string} />}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                    </>
                )}
            </Row>

            {/* Preview */}
            {criteria === 'KMS' && renderKmsPreview()}
            {criteria === 'HOURS' && renderHoursPreview()}
            {criteria === 'DATE' && renderDatePreview()}

            </div>
        </div>
    );
}

export default ReminderTriggerFormItem;
